import { VOICE_LEADING_IDIOMS } from '@tunasong/music-lib'
import type { VoiceLeadingIdiom } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import { useState, type FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import type { VoiceLeadingSubMode } from './voice-leading-lab-params.js'
import styles from './voice-leading-lab-welcome.module.css'

export interface VoiceLeadingLabWelcomeProps {
  preSelectedSubMode: VoiceLeadingSubMode | null
  preSelectedIdiom: VoiceLeadingIdiom
  onStartExplore: (idiom: VoiceLeadingIdiom) => void
  onStartQuiz: (idiom: VoiceLeadingIdiom) => void
}

const IDIOM_LABEL: Record<VoiceLeadingIdiom, string> = {
  classical: 'Classical',
  jazz: 'Jazz',
  pop: 'Pop',
}

/**
 * Voice-Leading Lab welcome surface. Two sub-mode cards (Explore / Quiz) plus an
 * idiom picker (Classical / Jazz / Pop) shared by both sub-modes — the selected
 * idiom is threaded into whichever sub-mode the user starts.
 *
 * Spec: docs/superpowers/specs/2026-05-27-voice-leading-lab-v1-design.md §9
 */
export const VoiceLeadingLabWelcome: FC<VoiceLeadingLabWelcomeProps> = props => {
  const { preSelectedSubMode, preSelectedIdiom, onStartExplore, onStartQuiz } = props
  const [idiom, setIdiom] = useState<VoiceLeadingIdiom>(preSelectedIdiom)

  const toolCopy = useTheoryCopy('voice-leading-lab')
  const exploreCopy = useTheoryCopy('voice-leading-explore')
  const quizCopy = useTheoryCopy('voice-leading-quiz')
  const idiomCopy = useTheoryCopy('vl-idiom-picker')

  return (
    <div className={styles.welcome}>
      <header className={styles.intro}>
        <h1 className={styles.title}>{toolCopy?.displayName ?? 'Voice-Leading Lab'}</h1>
        {toolCopy?.body ? <p className={styles.tagline}>{toolCopy.body}</p> : null}
      </header>

      <fieldset className={styles.idiomPicker}>
        <legend className={styles.idiomLegend}>{idiomCopy?.displayName ?? 'Idiom'}</legend>
        <div className={styles.idiomButtons}>
          {VOICE_LEADING_IDIOMS.map(option => (
            <Button
              key={option}
              type="button"
              variant={idiom === option ? 'contained' : 'outlined'}
              aria-pressed={idiom === option}
              onClick={() => setIdiom(option)}
              className={styles.idiomButton}
            >
              {IDIOM_LABEL[option]}
            </Button>
          ))}
        </div>
        {idiomCopy?.body ? <p className={styles.idiomBody}>{idiomCopy.body}</p> : null}
      </fieldset>

      <div className={styles.subModeGrid}>
        <Button
          type="button"
          variant="outlined"
          onClick={() => onStartExplore(idiom)}
          aria-pressed={preSelectedSubMode === 'explore'}
          className={styles.subModeCard}
        >
          <span className={styles.cardTitle}>{exploreCopy?.displayName ?? 'Voice-Leading Explore'}</span>
          {exploreCopy?.body ? <span className={styles.cardBody}>{exploreCopy.body}</span> : null}
        </Button>
        <Button
          type="button"
          variant="outlined"
          onClick={() => onStartQuiz(idiom)}
          aria-pressed={preSelectedSubMode === 'quiz'}
          className={styles.subModeCard}
        >
          <span className={styles.cardTitle}>{quizCopy?.displayName ?? 'Voice-Leading Quiz'}</span>
          {quizCopy?.body ? <span className={styles.cardBody}>{quizCopy.body}</span> : null}
        </Button>
      </div>
    </div>
  )
}
